import { Send } from 'lucide-react'
import type { ReactNode } from 'react'
import { Button } from '@/components/ui/button'

// `max-md:text-base` is not cosmetic: iOS Safari zooms the whole page in when a
// focused input is under 16px, and never zooms back out.
const TEXTAREA_CLASS =
  'min-h-[72px] w-full resize-y rounded-lg border border-border bg-white px-3 py-2 text-sm max-md:text-base text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-60'

/**
 * The message composer under a chat list — extracted from Journey's ChatPanel
 * (spec-27) for reuse by the global Sparky copilot (spec-29). Cmd/Ctrl+Enter
 * sends; `aboveSlot` renders above the textarea (e.g. picked-file chips) and
 * `leftAction` next to the send button (e.g. the attachment picker).
 */
export function ChatComposer({
  value,
  onChange,
  onSend,
  disabled,
  sendDisabled,
  placeholder,
  error,
  hint,
  aboveSlot,
  leftAction,
}: {
  value: string
  onChange: (value: string) => void
  onSend: () => void
  /** Locks the textarea and every action (run active / sending / gated). */
  disabled: boolean
  /** Extra send-only lock (e.g. empty input) on top of `disabled`. */
  sendDisabled?: boolean
  placeholder: string
  error?: string | null
  /** Muted line under the textarea, e.g. a Dutch gate explanation. */
  hint?: string | null
  aboveSlot?: ReactNode
  leftAction?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-2 border-t border-border pt-3">
      {aboveSlot}

      <textarea
        aria-label="Bericht"
        className={TEXTAREA_CLASS}
        placeholder={placeholder}
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault()
            onSend()
          }
        }}
      />

      {error ? (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      ) : null}
      {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}

      <div className="flex items-center justify-between gap-2">
        {leftAction ?? <span />}
        <Button
          type="button"
          size="sm"
          className="max-md:h-11 max-md:px-4"
          disabled={disabled || sendDisabled}
          onClick={onSend}
        >
          <Send className="size-3.5" aria-hidden />
          Versturen
        </Button>
      </div>
    </div>
  )
}
